import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import JourneyScene, { avatarFor } from "../../components/crossroads/JourneyScene";
import {
  CrossroadsPopup,
  FeedbackPopup,
  LearningContent,
  Spinner,
  StatTile,
  hasLearningContent,
} from "../../components/crossroads/CrossroadsUi";
import { selectAdminToken } from "../../admin/sessionSlice";
import { mergeReportId } from "../loginSlice";
import { completeJourney, getGameContent, saveFeedback, saveResponses, sessionToken, startJourney } from "../../functions/crossroadsApi";
import { extractReportIdFromResponse, resolveReportsAuthToken, sendReport } from "../../functions/sendReport";
import {
  correctLetter,
  isChoiceQuestion,
  formatDuration,
  pathColorsFor,
  pickMessage,
  resolveCrossroadsTheme,
  revealFor,
  seededShuffle,
  themeCssVars,
} from "../../utils/crossroadsTheme";
import { demoSubscribePopupAndRedirect, isUserDemoBypass } from "../../utils/demoGate";
import useHeaderOffset, { useBoardPageBody } from "../../utils/useHeaderOffset";
import "./Play.css";

const PROGRESS_KEY_PREFIX = "cr_progress_v1_";

function readProgress(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeProgress(key, value) {
  try {
    if (value) sessionStorage.setItem(key, JSON.stringify(value));
    else sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable — progress just won't survive a refresh */
  }
}

function useIsNarrow() {
  const query = "(max-width: 760px)";
  const [narrow, setNarrow] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const onChange = () => setNarrow(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

/** Self-paced journey (PRD 2.1 / 3 / 6). `?preview=1` plays the admin's unpublished draft. */
export default function Play() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preview = ["1", "true"].includes(String(params.get("preview") || "").toLowerCase());
  const user = useSelector((s) => s.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const headerOffset = useHeaderOffset();
  const narrow = useIsNarrow();
  useBoardPageBody();

  const token = preview ? adminToken : user ? sessionToken(user.sessionId, user.organizationId) : null;
  const playerKey = preview ? `preview_${adminToken || ""}` : user ? `${user.userId}_${user.sessionId}` : "";
  const progressKey = PROGRESS_KEY_PREFIX + playerKey;

  const [content, setContent] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [screen, setScreen] = useState("intro"); // intro | journey | complete
  const [phase, setPhase] = useState("idle");
  const [checkpointKey, setCheckpointKey] = useState(0);
  const [runNonce, setRunNonce] = useState(0);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [popup, setPopup] = useState(null); // outcome | learning | feedback
  const [answers, setAnswers] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  const [result, setResult] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [now, setNow] = useState(Date.now());
  const decideAtRef = useRef(0);
  const restoredRef = useRef(false);
  const retryOnLoadRef = useRef(false);

  // ---- Load content ----
  useEffect(() => {
    if (!token) {
      setLoadError(
        preview
          ? "Open Preview from the admin panel so it can use your admin session."
          : "Please open Crossroads from your ExtraMilePlay game link."
      );
      return;
    }
    let cancelled = false;
    getGameContent({ token, draft: preview })
      .then((data) => {
        if (!cancelled) setContent(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message || "Failed to load the game.");
      });
    return () => {
      cancelled = true;
    };
  }, [token, preview]);

  const theme = useMemo(() => resolveCrossroadsTheme(content?.settings), [content]);

  // Live Audience games are played from the voting screen, not here.
  useEffect(() => {
    if (content && !preview && theme.mode === "live") navigate("/vote", { replace: true });
  }, [content, preview, theme.mode, navigate]);

  const questions = useMemo(() => {
    const list = content?.questions || [];
    return theme.shuffle ? seededShuffle(list, `${playerKey}-${runNonce}`) : list;
  }, [content, theme.shuffle, playerKey, runNonce]);
  const total = questions.length;
  const question = questions[index] || null;
  const colors = useMemo(() => (question ? pathColorsFor(question, theme) : { a: theme.colors.pathA, b: theme.colors.pathB }), [question, theme]);

  // ---- Resume after a refresh ----
  useEffect(() => {
    if (!content || restoredRef.current) return;
    restoredRef.current = true;
    const saved = readProgress(progressKey);
    // A fresh journey opens on the admin's introduction (Game Settings → Game Content);
    // "Begin Journey" there starts it.
    const startFresh = () => {
      if (!(content.questions || []).length) return;
      setPopup("intro");
    };
    if (!saved || (saved.screen !== "journey" && saved.screen !== "complete")) {
      startFresh();
      return;
    }
    setRunNonce(saved.runNonce || 0);
    setAnswers(Array.isArray(saved.answers) ? saved.answers : []);
    setStartedAt(saved.startedAt || null);
    if (saved.screen === "complete" && saved.result) {
      retryOnLoadRef.current = Boolean(saved.result.saveFailed);
      setResult(saved.result);
      setScreen("complete");
      setPhase("done");
    } else if (saved.screen === "journey") {
      const list = Array.isArray(saved.answers) ? saved.answers : [];
      const last = list[list.length - 1];
      if (last && !last.learned) {
        // Refreshed while this crossroads' outcome / learning moment was on screen: bring it
        // back (the learning step is never skipped) before the journey carries on.
        setIndex(last.questionIndex);
        setSelected(last.selected);
        setScreen("journey");
        setPhase("hold");
        setPopup(saved.popup === "learning" ? "learning" : "outcome");
        setCheckpointKey((k) => k + 1);
        return;
      }
      const next = Math.min(list.length, (content.questions || []).length);
      setIndex(next);
      setScreen("journey");
      // Everything answered already → go straight to the finish.
      setPhase(next >= (content.questions || []).length ? "arrive" : "approach");
      setCheckpointKey((k) => k + 1);
    }
  }, [content, progressKey]);

  useEffect(() => {
    if (!content || !restoredRef.current) return;
    writeProgress(progressKey, { runNonce, answers, startedAt, screen, result, popup });
  }, [content, progressKey, runNonce, answers, startedAt, screen, result, popup]);

  const beginJourney = () => {
    // The server measures the completion time from here (the local clock only drives the HUD).
    if (!preview && user) {
      startJourney({ token, userId: user.userId, name: user.name, email: user.email, userType: user.role || user.source, reportId: user.reportId }).catch(
        (err) => console.warn("startJourney failed:", err?.message || err)
      );
    }
    setPopup(null);
    setStartedAt(Date.now());
    setNow(Date.now());
    setScreen("journey");
    setPhase("approach");
    setCheckpointKey((k) => k + 1);
  };

  // Live timer for the HUD.
  useEffect(() => {
    if (screen !== "journey") return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [screen]);

  const score = answers.reduce((s, a) => s + (a.points || 0), 0);
  // Journey progress: crossroads decided so far out of the whole journey (PRD HUD).
  const decided = Math.min(answers.length, total);
  const progressPct = total ? Math.round((decided / total) * 100) : 0;
  const elapsedSeconds = startedAt ? Math.max(0, Math.floor((now - startedAt) / 1000)) : 0;

  const updateAnswer = useCallback((questionId, patch) => {
    setAnswers((prev) => prev.map((a) => (a.questionId === questionId ? { ...a, ...patch } : a)));
  }, []);

  /**
   * Players' content has no answers in it (they'd be readable before choosing): the server
   * records the choice and replies with the verdict, points and learning content for it.
   */
  const judgeAnswer = useCallback(
    async (answer, { retries = 1 } = {}) => {
      updateAnswer(answer.questionId, { pending: true, error: false });
      try {
        const res = await saveResponses({
          token,
          userId: user.userId,
          name: user.name,
          email: user.email,
          mode: "self_paced",
          responses: [{ questionId: answer.questionId, questionIndex: answer.questionIndex, selected: answer.selected, responseMs: answer.responseMs }],
        });
        const r = (res.results || []).find((x) => String(x.questionId) === String(answer.questionId));
        if (!r || !r.reveal) throw new Error("No verdict");
        updateAnswer(answer.questionId, {
          pending: false,
          error: false,
          correct: r.isCorrect,
          points: r.points || 0,
          reveal: r.reveal,
        });
      } catch {
        if (retries > 0) {
          window.setTimeout(() => judgeAnswer(answer, { retries: retries - 1 }), 2500);
        } else {
          updateAnswer(answer.questionId, { pending: false, error: true });
        }
      }
    },
    [token, user, updateAnswer]
  );

  // After a refresh, any answer still waiting for its verdict is asked for again.
  const rejudgedRef = useRef(false);
  useEffect(() => {
    if (preview || !user || !content || !restoredRef.current || rejudgedRef.current) return;
    rejudgedRef.current = true;
    answers.filter((a) => !a.reveal).forEach((a) => judgeAnswer(a));
  }, [preview, user, content, answers, judgeAnswer]);

  const choose = (side) => {
    if (phase !== "decide" || selected || !question) return;
    const answer = {
      questionId: question.id,
      questionIndex: index,
      selected: side,
      responseMs: decideAtRef.current ? Date.now() - decideAtRef.current : null,
    };
    setSelected(side);
    setPhase("travel");
    if (preview || !user) {
      // Admin preview plays the draft, which (admin-only) still carries the answers.
      const right = correctLetter(question);
      const isCorrect = right ? side === right : null; // null = choice based: no right road
      setAnswers((prev) => [
        ...prev.filter((a) => a.questionId !== question.id),
        {
          ...answer,
          correct: isCorrect,
          points: isCorrect ? Number(question.points) || 0 : 0,
          reveal: revealFor(question),
        },
      ]);
      return;
    }
    setAnswers((prev) => [...prev.filter((a) => a.questionId !== question.id), { ...answer, pending: true }]);
    judgeAnswer(answer);
  };

  const finishJourney = useCallback(async () => {
    const timeSeconds = startedAt ? Math.max(1, Math.round((Date.now() - startedAt) / 1000)) : 0;
    const scored = answers.filter((a) => a.correct != null);
    const correct = scored.filter((a) => a.correct).length;
    const local = {
      points: answers.reduce((s, a) => s + (a.points || 0), 0),
      correct,
      incorrect: scored.length - correct,
      accuracy: scored.length ? Math.round((correct / scored.length) * 100) : 0,
      timeSeconds,
      alreadyCompleted: false,
    };
    if (preview || !user) {
      setResult(local);
      setScreen("complete");
      return;
    }

    setFinishing(true);
    let summary = local;
    try {
      // Re-send every answer (first answer per crossroads wins server-side) so a dropped
      // request mid-journey can't cost the player their score.
      await saveResponses({
        token,
        userId: user.userId,
        name: user.name,
        email: user.email,
        mode: "self_paced",
        responses: answers.map(({ questionId, questionIndex, selected: choice, responseMs }) => ({
          questionId,
          questionIndex,
          selected: choice,
          responseMs,
        })),
      });
      const server = await completeJourney({
        token,
        userId: user.userId,
        name: user.name,
        email: user.email,
        mode: "self_paced",
        timeSeconds,
        userType: user.role || user.source,
        reportId: user.reportId,
      });
      summary = { ...local, alreadyCompleted: server.alreadyCompleted, timeSeconds: server.reportedTimeSeconds ?? local.timeSeconds };
      if (!server.alreadyCompleted) {
        summary = { ...summary, points: server.points, correct: server.correct, incorrect: server.incorrect, accuracy: server.accuracy };
      }

      if (!isUserDemoBypass(user)) {
        const reportToken = resolveReportsAuthToken(user.token, token);
        const role = String(user.role || user.source || "ORG_USER").toUpperCase();
        sendReport({
          token: reportToken,
          reportId: user.reportId || null,
          userId: user.userId,
          name: user.name,
          email: user.email || null,
          sessionId: user.sessionId,
          organizationId: user.organizationId,
          gameId: user.gameId,
          role,
          user_type: role,
          owner: role,
          points: server.reportedPoints,
          accumulatePoints: false,
          time: server.reportedTimeSeconds,
          ans: JSON.stringify({ correct: server.correct, incorrect: server.incorrect, accuracy: server.accuracy }),
        })
          .then((res) => {
            const rid = extractReportIdFromResponse(res);
            if (rid) dispatch(mergeReportId(rid));
          })
          .catch((err) => console.warn("Platform report failed:", err?.message || err));
      }
    } catch (err) {
      console.error("Failed to save the journey result:", err);
      summary = { ...local, saveFailed: true, saveError: err?.message || "" };
    } finally {
      setFinishing(false);
    }
    setResult(summary);
    setScreen("complete");
  }, [answers, startedAt, preview, user, token, dispatch]);

  // A result that failed to save is sent again automatically when the page is reloaded
  // (retryOnLoadRef is set by the restore above once the saved state is back).
  useEffect(() => {
    if (!retryOnLoadRef.current || finishing || screen !== "complete" || !result?.saveFailed) return;
    retryOnLoadRef.current = false;
    finishJourney();
  }, [screen, result, finishing, finishJourney]);

  const onPhaseDone = useCallback(
    (done) => {
      if (done === "approach") {
        decideAtRef.current = Date.now();
        setPhase("decide");
      } else if (done === "travel") {
        setPhase("hold");
        setPopup("outcome");
      } else if (done === "merge") {
        if (index + 1 < total) {
          setIndex(index + 1);
          setSelected(null);
          setPhase("approach");
          setCheckpointKey((k) => k + 1);
        } else {
          setPhase("arrive");
        }
      } else if (done === "arrive") {
        setPhase("done");
        finishJourney();
      }
    },
    [index, total, finishJourney]
  );

  // Every decision is followed by its learning moment (PRD 3.7) — never skipped.
  const continueFromOutcome = () => setPopup("learning");

  const continueJourney = () => {
    // Demo sessions only get a taste of the first crossroads, then a CTA to subscribe.
    // It is not marked learned, so a refresh brings this step back instead of skipping the gate.
    if (!preview && isUserDemoBypass(user)) {
      demoSubscribePopupAndRedirect();
      return;
    }
    // This crossroads is finished (outcome + learning seen); a refresh now moves on to the next.
    if (question) updateAnswer(question.id, { learned: true });
    setPopup(null);
    setPhase("merge");
  };

  const replay = () => {
    if (!preview && isUserDemoBypass(user)) {
      demoSubscribePopupAndRedirect();
      return;
    }
    writeProgress(progressKey, null);
    setRunNonce((n) => n + 1);
    setAnswers([]);
    setStartedAt(Date.now());
    setNow(Date.now());
    setResult(null);
    setIndex(0);
    setSelected(null);
    setPopup(null);
    setScreen("journey");
    setPhase("approach");
    setCheckpointKey((k) => k + 1);
  };

  // Completion popup → feedback → leaderboard (the admin preview just starts over).
  const finishAndLeave = () => {
    if (preview) {
      replay();
      return;
    }
    setPopup("feedback");
  };
  const goToLeaderboard = () => navigate("/leaderboard", { replace: true });

  // ---- Render ----
  const rootStyle = { ...themeCssVars(theme), "--cr-top": `${headerOffset}px` };

  if (loadError) {
    return (
      <div className="cr-root cr-play" style={rootStyle}>
        <div className="cr-center">
          <p className="cr-error">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!content) {
    return (
      <div className="cr-root cr-play" style={rootStyle}>
        <Spinner label="Loading your journey" />
      </div>
    );
  }

  const upcoming = questions[index + 1];
  const nextCrossroads = upcoming
    ? {
        label: `Scenario ${index + 2}`,
        text: upcoming.question_name,
        optionA: upcoming.option_one,
        optionB: upcoming.option_two,
        pathColors: pathColorsFor(upcoming, theme),
      }
    : null;
  const currentAnswer = answers.find((a) => a.questionId === question?.id);
  const outcomeCorrect = currentAnswer?.correct;
  const reveal = currentAnswer?.reveal || null;
  const judging = Boolean(currentAnswer?.pending || currentAnswer?.error) || !reveal;
  const isChoice = isChoiceQuestion(question);
  const chosenText = selected === "A" ? question?.option_one : question?.option_two;
  let outcomeHeading = isChoice
    ? `You chose: ${chosenText || ""}`
    : pickMessage(outcomeCorrect ? theme.successMessages : theme.incorrectMessages, `${question?.id}-${selected}`);
  if (judging && !isChoice) outcomeHeading = currentAnswer?.error ? "We couldn't check your answer" : "Checking your answer…";
  const showDecision = screen === "journey" && phase === "decide" && question;

  return (
    <div className={`cr-root cr-play${narrow ? " is-narrow" : ""}`} style={rootStyle}>
      {preview ? <div className="cr-banner">Preview — results are not saved</div> : null}

      <div className="cr-play-stage">
        <JourneyScene
          phase={screen === "intro" ? "idle" : phase}
          checkpointKey={checkpointKey}
          pathColors={colors}
          optionA={question?.option_one}
          optionB={question?.option_two}
          scenarioLabel={question ? `Scenario ${index + 1}` : ""}
          scenarioText={screen === "journey" ? question?.question_name : ""}
          idleTitle={theme.title}
          idleText=""
          showText
          backgroundUrl={theme.images.journeyDesktop}
          backgroundUrlPortrait={theme.images.journeyPortrait}
          next={nextCrossroads}
          destinationNext={screen === "journey" && index + 1 >= total}
          avatars={[avatarFor(user?.userId || "preview", user?.name)]}
          selected={selected}
          outcome={phase === "hold" && currentAnswer && currentAnswer.correct != null ? (currentAnswer.correct ? "success" : "fail") : null}
          interactive={showDecision}
          onChoose={choose}
          onPhaseDone={onPhaseDone}
          insetTop={headerOffset + (narrow ? 34 : 0)}
        />

        {screen === "journey" ? (
          <div
            className="cr-vprogress"
            role="progressbar"
            aria-label="Journey progress"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progressPct}
            aria-valuetext={`${decided} of ${total} crossroads completed`}
          >
            <span className="cr-vprogress-count">
              {decided}/{total}
            </span>
            <span className="cr-vprogress-track" aria-hidden>
              <span className="cr-vprogress-fill" style={{ height: `${progressPct}%` }} />
            </span>
            <span className="cr-vprogress-pct">{progressPct}%</span>
          </div>
        ) : null}

        {screen === "journey" ? (
          <div className="cr-hud">
            <div className="cr-hud-spacer" />
            {theme.hasCorrectAnswers ? (
              <div className="cr-hud-pill" aria-label={`Score ${score}`}>
                ★ {score}
              </div>
            ) : null}
            {theme.showCompletionTime ? (
              <div className="cr-hud-pill" aria-label="Time elapsed">
                ⏱ {formatDuration(elapsedSeconds)}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {screen === "intro" && !total ? (
        <div className="cr-cover cr-cover--tint">
          <p className="cr-error">No crossroads have been set up for this game yet.</p>
        </div>
      ) : null}

      {finishing ? (
        <div className="cr-cover cr-cover--tint">
          <Spinner label="Saving your journey" />
        </div>
      ) : null}

      <CrossroadsPopup
        open={popup === "intro" && screen === "intro"}
        tone="neutral"
        eyebrow="Introduction"
        title={theme.introTitle}
        backgroundUrl={theme.images.popup}
        theme={theme}
        labelledBy="cr-intro-title"
        actions={
          <button type="button" className="cr-btn" onClick={beginJourney}>
            Begin Journey <span aria-hidden>→</span>
          </button>
        }
      >
        <p className="cr-complete-text">{theme.introText}</p>
      </CrossroadsPopup>

      <CrossroadsPopup
        open={popup === "outcome" && Boolean(question)}
        tone={judging || isChoice ? "neutral" : outcomeCorrect ? "success" : "fail"}
        eyebrow={judging || isChoice ? "Your choice" : outcomeCorrect ? "✓ Correct path" : "✗ Not this time"}
        title={outcomeHeading}
        backgroundUrl={theme.images.popup}
        theme={theme}
        labelledBy="cr-outcome-title"
        actions={
          currentAnswer?.error ? (
            <button type="button" className="cr-btn" onClick={() => judgeAnswer(currentAnswer)}>
              Retry
            </button>
          ) : (
            <button type="button" className="cr-btn" onClick={continueFromOutcome} disabled={judging}>
              Continue
            </button>
          )
        }
      >
        {judging ? (
          currentAnswer?.error ? (
            <p className="cr-muted">Check your connection, then retry — your choice is kept.</p>
          ) : (
            <Spinner label="Checking your answer" />
          )
        ) : null}
        {judging ? null : isChoice ? (
          <p className="cr-muted">There's no single right road here — this one was your call.</p>
        ) : outcomeCorrect ? (
          <span className="cr-points-chip">+{currentAnswer?.points || 0} points</span>
        ) : (
          <p className="cr-muted">
            The better road was <strong>{reveal?.correct === "A" ? question?.option_one : question?.option_two}</strong>.
          </p>
        )}
      </CrossroadsPopup>

      <CrossroadsPopup
        open={popup === "learning" && Boolean(question)}
        tone="learning"
        eyebrow="💡 Learning moment"
        title={reveal?.dyk_heading || theme.dykHeading}
        backgroundUrl={theme.images.popup}
        theme={theme}
        labelledBy="cr-learning-title"
        actions={
          <button type="button" className="cr-btn" onClick={continueJourney}>
            {index + 1 < total ? "Continue Journey" : "Reach the Destination"}
          </button>
        }
      >
        {reveal && hasLearningContent(reveal) ? (
          <LearningContent question={reveal} />
        ) : (
          <p className="cr-muted">Take a moment to think about how this choice played out.</p>
        )}
      </CrossroadsPopup>

      <CrossroadsPopup
        open={screen === "complete" && Boolean(result) && popup !== "feedback"}
        tone="success"
        eyebrow="🏁 Destination reached"
        title={theme.completionTitle}
        backgroundUrl={theme.images.popup}
        theme={theme}
        labelledBy="cr-complete-title"
        actions={
          result?.saveFailed ? (
            <>
              <button type="button" className="cr-btn" onClick={finishJourney} disabled={finishing}>
                {finishing ? "Retrying…" : "Retry"}
              </button>
              <button type="button" className="cr-btn-ghost" onClick={finishAndLeave} disabled={finishing}>
                Continue anyway
              </button>
            </>
          ) : (
            <button type="button" className="cr-btn" onClick={finishAndLeave}>
              OK
            </button>
          )
        }
      >
        {theme.completionMessage ? <p className="cr-complete-text">{theme.completionMessage}</p> : null}
        {result ? (
          <div className="cr-stats">
            {theme.hasCorrectAnswers ? (
              <>
                <StatTile label="Total score" value={result.points} />
                <StatTile label="Correct" value={result.correct} />
                <StatTile label="Incorrect" value={result.incorrect} />
                <StatTile label="Accuracy" value={`${result.accuracy}%`} />
              </>
            ) : (
              <StatTile label="Crossroads" value={total} />
            )}
            {theme.showCompletionTime ? <StatTile label="Time" value={formatDuration(result.timeSeconds)} /> : null}
          </div>
        ) : null}
        {result?.alreadyCompleted ? <p className="cr-note">Replays don't change your recorded score — your first journey counts.</p> : null}
        {result?.saveFailed ? (
          <p className="cr-note cr-note--warn" role="alert">
            We couldn't save this result{result.saveError ? ` (${result.saveError})` : ""}. Check your connection and press Retry —
            it is also retried automatically when you reload the page.
          </p>
        ) : null}
      </CrossroadsPopup>

      <FeedbackPopup
        open={popup === "feedback"}
        theme={theme}
        onSubmit={(feedback) => saveFeedback({ token, userId: user?.userId, feedback })}
        onDone={goToLeaderboard}
      />
    </div>
  );
}
